import React, { useCallback, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Card, CardBody } from "reactstrap";
import classnames from "classnames";
import { depotHistoryColumns, TABS, TABS_BUTTONS } from "pages/depot-management/depotDetails/constants";
import DepotInformation from "pages/depot-management/depotModal/Tabs/DepotInformation";
import GeoDetails from "pages/depot-management/depotModal/Tabs/GeoDetails";
import CustomButton from "components/Common/buttons/CustomButton";
import NGITable from "components/Common/NGITable";
import NGIPrompt from "components/Common/NGIPrompt";
import Sectors from "pages/depot-management/depotDetails/Tabs/Sectors";
import MiniDepots from "pages/depot-management/depotDetails/Tabs/MiniDepots";
import { userHasOneOfRoles } from "utils/helpers/functions";
import { USER_ROLES } from "utils/constants";

const useStyles = makeStyles({
  root: {
    width: "100%",
    marginTop: 30,
    display: "flex",
  },
  container: {
    borderRadius: 30,
    width: "100%",
  },
  tabs: {
    display: "flex",
    padding: "30px 0",
    fontSize: 13,
    fontWeight: 600,
    flexDirection: "column",
    marginRight: -4,
  },
  tab: {
    backgroundColor: "#E1E1E1",
    padding: 15,
    borderRadius: "10px 0 0 10px",
    marginBottom: 10,
    whiteSpace: "nowrap",

    "&:hover": {
      cursor: "pointer",
    },
  },
  active: {
    backgroundColor: "#FFFFFF!important",
  },
  hide: {
    display: "none",
  },
  show: {
    display: "block",
  },
  actionContainer: {
    marginTop: 16,
  },
  editContainer: {
    display: "flex",
    "&> div": {
      width: "50%",
    },
  },
});

const DataTabs = ({
  depot,
  depotsHistory,
  depotForm,
  isLoaded,
  onClearMap,
  onPolygonComplete,
  onMarkerComplete,
  isTouched,
  handleSave,
  isHistoryLoading,
  polygonList,
  TSRList,
  updateData,
  miniDepots,
  setLoader,
  isLoading,
}) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(TABS.EDIT_GEOFENCE);
  const [isDirty, setIsDirty] = useState(false);

  const onTabClick = useCallback(e => {
    const tab = e.target.dataset.tab;
    setActiveTab(tab);
  }, []);

  const { marker, polygon } = useMemo(
    () => ({
      marker: depot ? { lat: +depot?.location?.lat, lng: +depot?.location?.lon } : {},
      polygon: depot ? depot?.boundaries?.coordinates[0] : [],
    }),
    [depot]
  );
  const onFieldsChanged = useCallback(() => setIsDirty(true), []);

  const onPolygonCompleteWrapper = useCallback(
    (e, f) => {
      onPolygonComplete(e, f);
      setIsDirty(true);
    },
    [onPolygonComplete]
  );

  const onMarkerCompleteWrapper = useCallback(
    e => {
      onMarkerComplete(e);
      setIsDirty(true);
    },
    [onMarkerComplete]
  );

  const onClearMapWrapper = useCallback(
    e => {
      onClearMap(e);
      setIsDirty(true);
    },
    [onClearMap]
  );

  const onSaveWrapper = useCallback(
    e => {
      setIsDirty(false);
      handleSave(e);
    },
    [handleSave]
  );

  const center = useMemo(() => ({ ...(polygon && { lat: polygon[0][0], lng: polygon[0][1] }) }), []);

  return (
    <div className={classes.root}>
      <NGIPrompt show={isDirty} />
      <div className={classes.tabs}>
        {TABS_BUTTONS.map(item => {
          if (item?.roles && !userHasOneOfRoles(item?.roles)) {
            return null;
          }
          if (item?.hideRoles && userHasOneOfRoles(item?.hideRoles)) {
            return null;
          }
          return (
            <div
              key={item.key}
              className={classnames(classes.tab, activeTab === item.key && classes.active)}
              data-tab={item.key}
              onClick={onTabClick}
            >
              {item.title}
            </div>
          );
        })}
      </div>
      <Card className={classes.container}>
        <CardBody>
          {activeTab === TABS.EDIT_GEOFENCE && (
            <>
              <div className={classes.editContainer}>
                <div>
                  <DepotInformation isNew={false} form={depotForm} onChange={onFieldsChanged} />
                </div>
                <div>
                  <GeoDetails
                    polygonList={polygonList}
                    center={marker.lat ? marker : center}
                    initialData={{ marker, polygon }}
                    isLoaded={isLoaded}
                    onClearMap={onClearMapWrapper}
                    onPolygonComplete={onPolygonCompleteWrapper}
                    onMarkerComplete={onMarkerCompleteWrapper}
                    isTouched={isTouched}
                    allowSearch
                  />
                </div>
              </div>
              <div className={classes.actionContainer}>
                <CustomButton
                  hideRoles={[USER_ROLES.MDSC]}
                  type="primary"
                  size="small"
                  text="Save"
                  onClick={onSaveWrapper}
                />
              </div>
            </>
          )}
          {activeTab === TABS.SECTORS && <Sectors updateData={updateData} sectors={depot?.sectors} TSRList={TSRList} />}
          {activeTab === TABS.DEPOT_HISTORY && (
            <NGITable isLoading={isHistoryLoading} columns={depotHistoryColumns()} data={depotsHistory} />
          )}
          {activeTab === TABS.MINI_DEPOTS && (
            <MiniDepots
              setLoader={setLoader}
              isLoading={isLoading}
              mainDepotId={depot?.id}
              mainDepotName={depot?.name}
              miniDepots={miniDepots}
              updateData={updateData}
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default DataTabs;
